import React, { useState, useEffect, useRef } from 'react';
import { Cross, Add, ReceiptEdit } from '../../CustomComponents/SwiftIcon/Icons';
import SwiftModal from '../../CustomComponents/SwiftModal/SwiftModal';
import ConfirmBox from '../../CustomComponents/ConfirmBox/ConfirmBox';
import { COLOR_VARS } from '../../../exports/ColorVars';
import CustomIconButton from '../../Body/CustomChartComponents/CustomIconButton/CustomIconButton';
import { GetCookie } from '../../../exports/CookieManagement';
import ServerRequest from '../../../exports/ServerRequest';
import '../../../css/RaiseTicket/RaiseTicket.css';
import '../../../css/AdminManagement/AdminTable.css';
import Pulse from '../../Loader/Pulse';
import { ArrowUp2, ArrowRight2 } from '../../CustomComponents/SwiftIcon/Icons';

const REQUEST_BASE_URL = process.env.REACT_APP_REQUEST_BASE_URL;
 const token = GetCookie('admin_token');

const TicketsManagement = () => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [filterStatus, setFilterStatus] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [conversations, setConversations] = useState([]);
    const [conversationLoading, setConversationLoading] = useState(false);
    const [conversationError, setConversationError] = useState('');
    const [newMessage, setNewMessage] = useState('');
    const [conversationFiles, setConversationFiles] = useState([]);
    const [sendingMessage, setSendingMessage] = useState(false);
    const messagesEndRef = useRef(null);

    // Helper function to truncate file names
    const truncateFileName = (fileName, maxLength = 20) => {
        if (!fileName) return 'Unknown File';
        if (fileName.length <= maxLength) return fileName;
        const extension = fileName.split('.').pop();
        const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
        const truncatedName = nameWithoutExt.substring(0, maxLength - extension.length - 4);
        return `${truncatedName}...${extension}`;
    };

    // Convert text to HTML preserving spaces and line breaks
    const convertToHtml = (text) => {
        if (!text) return '';
        return text
            .replace(/ /g, '&nbsp;') // Replace spaces with non-breaking spaces
            .replace(/\n/g, '<br>'); // Replace line breaks with <br> tags
    };

    // Auto-scroll to bottom of messages
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        fetchTickets();
    }, [token]);

    useEffect(() => {
        if (conversations.length > 0) {
            scrollToBottom();
        }
    }, [conversations]);

    const fetchTickets = async () => {
        try {
            setLoading(true);
            setError('');

            const response = await ServerRequest({
                url: `/admin/tickets?token=${token}`,
                method: 'get'
            });

            if (response && response.error === false && response.data) {
                setTickets(response.data);
                console.log('Admin tickets loaded:', response.data.length);
            } else if (response && response.data) {
                setTickets(response.data);
            } else {
                console.log('No tickets data in response:', response.data);
                setTickets([]);
            }
        } catch (error) {
            setError(`Failed to load tickets: ${error.response?.data?.message || error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const fetchLatestConversation = async (ticketId) => {
        try {
            setConversationLoading(true);
            setConversationError('');

            const response = await ServerRequest({
                url: `/admin/tickets/${ticketId}/conversation?token=${token}`,
                method: 'get'
            });

            if (response && !response.error) {
                setConversations(response.data || []);
            } else {
                setConversationError('Failed to load conversation');
            }
        } catch (error) {
            console.error('Error fetching conversation:', error);
            setConversationError('Failed to load conversation');
        } finally {
            setConversationLoading(false);
        }
    };

    const handleTicketClick = (ticket) => {
        setSelectedTicket(ticket);
        setShowModal(true);
        // Fetch the latest conversation from server instead of using cached data
        fetchLatestConversation(ticket.ticketId);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedTicket(null);
        setConversations([]);
        setNewMessage('');
        setConversationFiles([]);
        setConversationError('');
        fetchTickets();
    };

    const handleConversationFileChange = (e) => {
        const files = Array.from(e.target.files);
        const validFiles = [];
        const errors = [];

        files.forEach(file => {
            const validTypes = ['image/jpeg', 'image/jpg', 'application/pdf', 'image/png', 'image/gif'];
            if (!validTypes.includes(file.type)) {
                errors.push(`${file.name}: Only JPG, JPEG, PNG, GIF, and PDF files are allowed`);
                return;
            }

            const maxSize = 5 * 1024 * 1024;
            if (file.size > maxSize) {
                errors.push(`${file.name}: File size must be less than 5MB`);
                return;
            }

            validFiles.push(file);
        });

        if (errors.length > 0) {
            setConversationError(errors.join(', '));
            return;
        }

        setConversationFiles(prev => [...prev, ...validFiles]);
        setConversationError('');
    };

    const removeConversationFile = (index) => {
        setConversationFiles(prev => prev.filter((_, i) => i !== index));
    };

    const sendMessage = async () => {
        if (!newMessage && conversationFiles.length === 0) return;
        if (sendingMessage) return; // Prevent double sending

        try {
            setSendingMessage(true);

            // Create FormData for file uploads
            const formData = new FormData();
            formData.append('ticketId', selectedTicket.ticketId);
            formData.append('message', newMessage);

            // Add files to FormData
            conversationFiles.forEach((file) => {
                formData.append('files', file);
            });

            const response = await ServerRequest({
                url: `/admin/tickets/send-message?token=${token}`,
                method: 'post',
                data: formData
            });

            if (response && !response.error) {
                setNewMessage('');
                setConversationFiles([]);
                // Refresh the conversation to get the latest messages from TXT file
                fetchLatestConversation(selectedTicket.ticketId);
            } else {
                setConversationError('Failed to send message');
            }
        } catch (error) {
            console.error('Error sending message:', error);
            setConversationError('Failed to send message');
        } finally {
            setSendingMessage(false);
        }
    };

    const handleTicketStatusChange = async (ticketId, isChecked, event) => {
        event.stopPropagation(); // Prevent opening the modal

        const newStatus = isChecked ? 'closed' : 'open';

        // Immediately update the ticket status in local state (optimistic update)
        setTickets(prevTickets =>
            prevTickets.map(ticket =>
                ticket.id === ticketId
                    ? { ...ticket, status: newStatus }
                    : ticket
            )
        );

        // Update selected ticket if it's the one being changed
        if (selectedTicket && selectedTicket.id === ticketId) {
            setSelectedTicket({ ...selectedTicket, status: newStatus });
        }

        try {

            await ServerRequest({
                url: `/admin/tickets/${ticketId}/status?token=${token}`,
                method: 'put',
                data: { status: newStatus }
            });

            console.log('Ticket status updated successfully');
        } catch (error) {
            console.error('Error updating ticket status:', error);
            setError('Failed to update ticket status');
            fetchTickets();
        }
    };

    const handleDeleteTicket = (ticketId, event) => {
        event.stopPropagation();

        ConfirmBox({
            title: 'Delete Ticket',
            description: 'Are you sure you want to delete this ticket? This action cannot be undone.',
            cancel: true,
            properties: [
                {
                    id: 'delete-confirm',
                    label: 'Delete',
                    handleFunction: async (closeConfirmBox) => {
                        // Immediately remove from UI (optimistic update)
                        setTickets(prevTickets =>
                            prevTickets.filter(ticket => ticket.id !== ticketId)
                        );

                        // Close modal if the deleted ticket was selected
                        if (selectedTicket && selectedTicket.id === ticketId) {
                            setShowModal(false);
                            setSelectedTicket(null);
                        }

                        try {
                            await ServerRequest({
                                url: `/admin/tickets/${ticketId}?token=${token}`,
                                method: 'delete'
                            });

                            console.log('Ticket deleted successfully');
                        } catch (error) {
                            console.error('Error deleting ticket:', error);
                            setError('Failed to delete ticket');

                            // If delete failed, we should reload the tickets to restore the UI
                            fetchTickets();
                        }

                        closeConfirmBox();
                    }
                }
            ]
        });
    };

    const filteredTickets = tickets.filter(ticket => {
        if (!searchTerm?.trim()) return true;

        const normalize = str =>
            (str || "")
                .toLowerCase()
                .replace(/[#\-\s]/g, ""); // remove #, -, and spaces

        const term = normalize(searchTerm);
        const accountName = normalize(ticket.account_name);
        const ticketId = normalize(ticket.ticketId);

        return accountName.includes(term) || ticketId.includes(term);
    });


    const finalFilteredTickets = filteredTickets.filter(ticket => {
        if (filterStatus === 'all') return true;
        return ticket.status === filterStatus;
    });

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className='admin-wrapper'>
                <div className="loading-container">
                    <div className="swift-spinner"></div>
                    <Pulse></Pulse>
{/* <p>Loading tickets...</p> */}
                </div>
            </div>
        );
    }

    return (
        <div className="support-tickets-container">
            <div className="tickets-header">
                <div className="header-content">
                    <h2>Support Tickets Management</h2>
                    <p>Manage and respond to customer support tickets</p>
                </div>
            </div>

            {error && (
                <div className="error-message">
                    {error}
                </div>
            )}

            {/* Search */}
            <div className="tickets-search">
                <div className="form-group">
                    <input
                        type="text"
                        className="form-input"
                        placeholder="Search by ticket id or client name ..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <div className="refresh-container">
                        <p
                            onClick={() => fetchTickets()}
                            disabled={loading}
                        >
                            Refresh
                        </p>
                    </div>
                </div>
            </div>

            {/* Tickets List */}
            <div className="tickets-content">
                {filteredTickets.length === 0 ? (
                    <div className="no-tickets">
                        <ReceiptEdit size={50} color={COLOR_VARS["SWIFT_COLOR4"]} />
                        <h3>No tickets found</h3>
                        <p>No support tickets match your current filters.</p>
                    </div>
                ) : (
                    <div className="tickets-list">
                        {finalFilteredTickets.map((ticket) => (
                            <div
                                key={ticket.id}
                                className={`ticket-card ${ticket.status === 'closed' ? 'closed' : ''}`}
                            >
                                <div className="ticket-header">
                                    <div className="ticket-id">
                                        <p><strong>Name:</strong> {ticket.account_name}</p>
                                        <div>{ticket.subject}</div>
                                        <p>{formatDate(ticket.createdAt)}</p>
                                        <p>#{ticket.ticketId}</p>
                                    </div>
                                    <div className="ticket-status-section">

                                        <div
                                            className="ticket-actions-container"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {(ticket.status === 'open' || ticket.status === 'pending') && (
                                                <span
                                                    className="close-text"
                                                    onClick={(e) => handleTicketStatusChange(ticket.id, true, e)}
                                                    title="Close Ticket"
                                                >
                                                    Close
                                                </span>
                                            )}
                                            <span
                                                className="delete-text"
                                                onClick={(e) => handleDeleteTicket(ticket.id, e)}
                                                title="Delete Ticket"
                                            >
                                                Delete
                                            </span>
                                        </div>
                                        <div
                                            onClick={() => handleTicketClick(ticket)}
                                            className={`ticket-status ${ticket.status}`}>
                                            {ticket.status}
                                        </div>
                                    </div>
                                </div>

                                <div className="ticket-details">
                                    <div className="ticket-info">
                                        <div className="ticket-files-section">
                                            <p>{ticket.lastmessage}</p>
                                            {ticket.filesUploaded > 0 && (
                                                <p><strong>Files:</strong> {ticket.filesUploaded} file(s) attached</p>
                                            )}
                                        </div>
                                        {ticket.filesUploaded > 0 && (
                                            <div>
                                                {(() => {
                                                    // Priority: Latest conversation files > Original ticket files
                                                    const filesToShow = ticket.latestConversationFiles && ticket.latestConversationFiles.length > 0
                                                        ? ticket.latestConversationFiles
                                                        : ticket.fileUrls;

                                                    return filesToShow && filesToShow.length > 0 && (
                                                        <div className="latest-files-preview">
                                                            <div className="files-preview-container">
                                                                {filesToShow.map((file, index) => (
                                                                    <div key={index} className="message-file">
                                                                        <a
                                                                            href={file.url}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                        >
                                                                            {truncateFileName(file.originalName)}
                                                                        </a>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Ticket Detail Modal */}
            {showModal && selectedTicket && (
                <SwiftModal
                    closeModal={closeModal}
                    top="2%"
                >
                    <div className="ticket-details-modal">
                        {/* Close Button Row */}
                        <div className="modal-header-content">
                            <div className="ticket-header-info">
                                <div className='ticket-header-info-badge-close'>
                                    <div className="ticket-id-badge">#{selectedTicket.ticketId}</div>
                                    <div className="modal-close" onClick={closeModal}>
                                        <Cross size={30} color={COLOR_VARS["SWIFT_COLOR4"]} />
                                    </div>
                                </div>
                                <div className="ticket-meta">
                                    <h3 className="ticket-subject">{selectedTicket.subject}</h3>
                                </div>
                            </div>
                        </div>

                        <div className="modal-wrapper">

                            {/* Conversation Body */}
                            <div className="modal-body">
                                <div className="conversation-messages">
                                    {sendingMessage ? (
                                        <div className="conversation-loading">
                                            <Pulse />
{/* <p>Sending message...</p> */}
                                        </div>
                                    ) : conversationLoading ? (
                                        <div className="conversation-loading">
                                            <Pulse />
{/* <p>Loading conversation...</p> */}
                                        </div>
                                    ) : conversationError ? (
                                        <p className="error-message">{conversationError}</p>
                                    ) : conversations.length === 0 ? (
                                        <div className="no-messages">
                                            <ReceiptEdit size={50} color={COLOR_VARS["SWIFT_COLOR4"]} />
                                            <p>No messages yet</p>
                                            <p>Start the conversation by sending a message below.</p>
                                        </div>
                                    ) : (
                                        <>
                                            {conversations.map((msg, index) => (
                                                <div key={index} className={`message ${msg.sender}`}>
                                                    <div className="message-card">
                                                        <div className="message-header">
                                                            <span className="sender-name">{msg.senderName}</span>
                                                            <span className="message-time">{formatDate(msg.timestamp)}</span>
                                                        </div>
                                                        <div className="message-content" dangerouslySetInnerHTML={{ __html: convertToHtml(msg.message) }}></div>
                                                        {msg.fileUrls && msg.fileUrls.length > 0 && (
                                                            <div className="message-files">
                                                                <span className="message-time">Files attached:</span>
                                                                {msg.fileUrls.map((file, fileIndex) => (
                                                                    <div key={fileIndex} className="message-file">
                                                                        <a href={file.url} target="_blank" rel="noopener noreferrer">
                                                                            {truncateFileName(file.originalName)}
                                                                        </a>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                            <div ref={messagesEndRef} />
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="modal-footer">
                                <div className="message-input-container">
                                    <div className='message-input-container-input'>
                                        <textarea
                                            value={newMessage}
                                            onChange={(e) => setNewMessage(e.target.value)}
                                            placeholder="Type your message here..."
                                            className="message-input"
                                        />
                                        {conversationFiles.length > 0 && (
                                            <div className="uploaded-files">
                                                {conversationFiles.map((file, index) => (
                                                    <div key={index} className="file-item">
                                                        <span className="file-name">{file.name}</span>
                                                        <span className="file-size">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                                                        <div
                                                            onClick={() => removeConversationFile(index)}
                                                        >
                                                            <Cross size={16} color={COLOR_VARS["SWIFT_COLOR4"]} />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    {/* File Upload for Conversation */}
                                    <div className="conversation-file-upload">
                                        <input
                                            type="file"
                                            multiple
                                            accept=".jpg,.jpeg,.png,.gif,.pdf"
                                            onChange={handleConversationFileChange}
                                            style={{ display: 'none' }}
                                            id="conversation-file-input"
                                        />
                                        <div className='file-uploads-button'>
                                            <label htmlFor="conversation-file-input" className="file-upload-label">
                                                <CustomIconButton
                                                    title="Upload Files"
                                                    icon={<Add size={20} color={COLOR_VARS["SWIFT_COLOR6"]} />}
                                                />
                                            </label>

                                            <label>
                                                <CustomIconButton
                                                    title="Send Message"
                                                    onClick={sendMessage}
                                                    disabled={(!newMessage && conversationFiles.length === 0) || sendingMessage}
                                                    icon={null}
                                                />
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </SwiftModal>
            )}
        </div>
    );
};

export default TicketsManagement;